import { zodResolver } from "@hookform/resolvers/zod";
import { composeProgress, isLoading } from "apollo/utils";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  DISCHARGE_LETTER_DOCS_FORM_TO_PATIENT_DOCUMENT,
  DOCS_FILL_FORM_OUTCOME,
  DOCS_FORM_NAMES,
  EXTRACT_OCR_STATUS,
  FILE_SCOPE,
  PATIENT_DOCUMENTS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  REHAB_FORM_TO_PATIENT_DOCUMENT,
  SOURCE_DOCUMENT_SOURCES,
  TRACK_EVENTS,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { useTriggerOperatorExecutor } from "core/model/careseekers/operatorExecutor";
import { isFileHISStateful } from "core/model/files/utils";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  Contradictions,
  DocsFeedbackRatingTypes,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  FileParams,
  FileState,
  FormMetadata,
  Patient,
  QueryProgress,
  RehabFormName,
  SourceFields,
} from "core/types";
import {
  CreateRehaPdfProps,
  useCreateRehaPdf,
} from "dsl/ecosystems/PatientForms/RehabForms/useCreateRehaPdf";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import {
  CreateTransitionalCarePdfProps,
  useCreateTransitionalCarePdf,
} from "dsl/ecosystems/PatientForms/useCreateTransitionalCarePdf";
import { useDocumentHandling } from "dsl/ecosystems/PatientForms/useDocumentHandling";
import { useImportFormData } from "dsl/ecosystems/PatientForms/utils";
import {
  applyFeedbackOverridesToSourceFields,
  FORM_NAME_TO_FILE_TYPE,
  getManuallyEditedFieldKeys,
  isDischargeLetterFormName,
  isRehabFormName,
  mergeSourceFieldMetadata,
  MigratedForms,
  REFRESH_MODE,
  RefreshMode,
  useSelectSourceDocumentFile,
} from "dsl/ecosystems/RecareDocs/Modal/utils";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Path, PathValue, useForm } from "react-hook-form";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { z } from "zod";
import { DOCS_FORM_FOOTER_SCREENS, DocsFormFooterScreen } from ".";
import { FOOTER_BANNERS, FooterBannerType } from "./Footer";
import { FormFieldConfig, PdfConfig, useGetFormFieldConfig } from "./Forms";
import {
  buildDischargeLetterObservation,
  FIELD_CONFIGS,
} from "./Forms/DischargeLetters/buildObservation";
import {
  CreateDischargePdfProps,
  useCreateFormPdf,
} from "./Forms/DischargeLetters/useCreateFormPdf";
import { getAuctionForm } from "./Forms/DischargeLetters/useGetForm";
import { HEADER_BANNERS, HeaderBannerAction } from "./HeaderBanners";
import { useDocsFillForm } from "./useDocsFillForm";
import { useFormDelete } from "./useFormDelete";
import { useFormPdfUpload } from "./useFormPdfCreation";
import { useFormSave } from "./useFormSave";

// to be deprecated in favour of joint docs fetch
export function useGetFormData<T extends DocsFormName>({
  auctionForms,
  consultationForms,
  formName,
}: {
  formName: T;
  auctionForms: AuctionForm[];
  consultationForms: AuctionForm[] | undefined;
}): {
  form: AuctionForm | null | undefined;
  formData: DocsFormDataMapped<T> | null | undefined;
  metadata?: FormMetadata<T>;
} {
  return useMemo(() => {
    const forms = auctionForms?.length ? auctionForms : consultationForms ?? [];

    return getAuctionForm({
      auctionForms: forms,
      formName,
    }) as {
      form: AuctionForm | null | undefined;
      formData: DocsFormDataMapped<T> | null | undefined;
      metadata?: FormMetadata<T>;
    };
  }, [formName, auctionForms, consultationForms]);
}

function usePdfHandlers<T extends DocsFormName>({
  auctionForms,
  config,
  files,
  footerScreen,
  formData,
  formId,
  formName,
  formType,
  isDirty,
  patient,
  setFooterBanner,
  setFooterScreen,
}: {
  auctionForms: AuctionForm[];
  files: { name: string; file: FileState }[];
  footerScreen: DocsFormFooterScreen;
  formData: DocsFormDataMapped<T> | undefined;
  formId: number | undefined;
  formName: T;
  formType: number | null | undefined;
  isDirty: boolean;
  patient: Patient;
  setFooterBanner: (banner: FooterBannerType | null) => void;
  setFooterScreen: Dispatch<SetStateAction<DocsFormFooterScreen>>;
  config: FormFieldConfig<T>;
}) {
  const translations = useTranslations();
  const triggerOperatorExecutorRequest = useTriggerOperatorExecutor();
  const externalId = patient.external_id ?? "";
  const urlCache = useRef(new Map<string, string>());
  const selectSourceDocumentFile = useSelectSourceDocumentFile({
    canUseExtractedDocument: false,
    externalId,
  });

  const matchedFile = useMemo(
    () =>
      files.find(
        ({ file }) =>
          "recare_file_type" in file &&
          file.recare_file_type === formType &&
          file.form_synced_with_file,
      ),
    [files, formType],
  );

  const getExistingFileUrl = useCallback(async () => {
    if (!matchedFile) return undefined;

    const result = await selectSourceDocumentFile({
      files,
      sourceDocument: {
        filename: matchedFile.name,
        id: String(matchedFile.file.id),
        is_deleted: false,
        kis_file_id: isFileHISStateful(matchedFile.file)
          ? matchedFile.file.id
          : null,
        recare_file_id: !isFileHISStateful(matchedFile.file)
          ? matchedFile.file.id
          : null,
        source: SOURCE_DOCUMENT_SOURCES.RECARE_FILE,
        url: "",
      },
      urlCache: urlCache.current,
    });
    return result?.url;
  }, [matchedFile, files, selectSourceDocumentFile]);

  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();

  const createFormPdf = useCreateFormPdf({
    auctionForms,
    externalId,
    formName,
  });

  const createDischargePdf = useCallback(
    async ({
      formData: data,
    }: {
      formData: DocsFormDataMapped<MigratedForms>;
    }) => {
      return createFormPdf({
        documentNumber: -1,
        formData: data,
        pdfConfig: config.pdfConfig as PdfConfig<MigratedForms>,
      });
    },
    [config.pdfConfig, createFormPdf],
  );

  const transitionalCareHandlers =
    useDocumentHandling<CreateTransitionalCarePdfProps>({
      careseekerName: patient.careseeker?.name ?? "",
      createPDFDocument: createTransitionalCarePdf,
      externalId,
      getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
      userId: patient.user_id ?? "",
    });

  const rehaHandlers = useDocumentHandling<CreateRehaPdfProps>({
    careseekerName: patient.careseeker?.name ?? "",
    createPDFDocument: createRehaPdf,
    externalId,
    getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
    userId: patient.user_id ?? "",
  });

  const dischargeHandlers = useDocumentHandling<CreateDischargePdfProps>({
    careseekerName: patient.careseeker?.name ?? "",
    createPDFDocument: createDischargePdf,
    externalId,
    getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
    userId: patient.user_id ?? "",
  });

  const isDischargeLetterForm = isDischargeLetterFormName(formName);

  const transitionalCareForm = auctionForms?.find(
    (form) => form.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
  );

  const triggerOperatorExecutor = useCallback(
    ({
      dischargeFormData,
      dischargeFormId,
      dischargeFormName,
    }: {
      dischargeFormData: DocsFormDataMapped<MigratedForms>;
      dischargeFormId: number;
      dischargeFormName: MigratedForms;
    }): Promise<boolean> =>
      triggerOperatorExecutorRequest({
        careseekerId: patient.careseeker?.id,
        fieldOrder: FIELD_CONFIGS[dischargeFormName].map((field) => field.key),
        formData: dischargeFormData,
        idempotencyKey: dischargeFormId,
        templateIdentifier: dischargeFormName,
      }),
    [patient.careseeker?.id, triggerOperatorExecutorRequest],
  );

  const handleOpenPdf = useCallback(async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF);
      return;
    }
    if (!matchedFile) return;
    let ok = false;
    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE) {
      ok = await transitionalCareHandlers.handleOpenPdf();
    } else if (isDischargeLetterForm) {
      ok = await dischargeHandlers.handleOpenPdf();
    } else {
      ok = await rehaHandlers.handleOpenPdf();
    }
    if (!ok) {
      setFooterBanner(FOOTER_BANNERS.OPEN_PDF_ERROR);
      return;
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
  }, [
    isDirty,
    footerScreen,
    setFooterScreen,
    matchedFile,
    formName,
    isDischargeLetterForm,
    dischargeHandlers,
    setFooterBanner,
    transitionalCareHandlers,
    rehaHandlers,
  ]);

  const handleSendToHis = useCallback(async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS);
      return;
    }

    let ok = false;
    let operatorExecutorOk = true;
    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE) {
      if (!transitionalCareForm) {
        setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_ERROR);
        return;
      }
      ok = await transitionalCareHandlers.handleSendToHis({
        documentType: PATIENT_DOCUMENTS.TRANSITIONAL_CARE,
        transitionalCare: transitionalCareForm,
      });
    } else if (isDischargeLetterForm) {
      if (!formId) {
        setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_ERROR);
        return false;
      }
      const dischargeFormData = formData as DocsFormDataMapped<MigratedForms>;
      const dischargeFormName = formName;
      const observation = buildDischargeLetterObservation(
        dischargeFormData,
        dischargeFormName,
        translations,
        externalId,
        formId,
      );
      ok = await dischargeHandlers.handleSendToHis({
        documentType:
          DISCHARGE_LETTER_DOCS_FORM_TO_PATIENT_DOCUMENT[dischargeFormName],
        formData: dischargeFormData,
        observation,
      });
      operatorExecutorOk = await triggerOperatorExecutor({
        dischargeFormData,
        dischargeFormId: formId,
        dischargeFormName,
      });
    } else if (isRehabFormName(formName)) {
      ok = await rehaHandlers.handleSendToHis({
        auctionForms,
        documentNumber: -1,
        documentType: REHAB_FORM_TO_PATIENT_DOCUMENT[formName],
        externalId,
        formData: formData as RehabFormDataMapped<RehabFormName>,
        formName,
      });
    }

    if (!ok) {
      setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_ERROR);
      return;
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
    setFooterBanner(
      operatorExecutorOk
        ? FOOTER_BANNERS.SEND_TO_HIS_SUCCESS
        : FOOTER_BANNERS.SEND_TO_OPERATOR_ERROR,
    );
  }, [
    isDirty,
    footerScreen,
    setFooterScreen,
    formName,
    isDischargeLetterForm,
    transitionalCareForm,
    transitionalCareHandlers,
    dischargeHandlers,
    rehaHandlers,
    auctionForms,
    formData,
    externalId,
    formId,
    translations,
    setFooterBanner,
    triggerOperatorExecutor,
  ]);

  const progressOpenPdf = composeProgress([
    transitionalCareHandlers.progressOpenPdf,
    dischargeHandlers.progressOpenPdf,
    rehaHandlers.progressOpenPdf,
  ]);

  const progressSendToHis = composeProgress([
    transitionalCareHandlers.progressSendToHis,
    dischargeHandlers.progressSendToHis,
    rehaHandlers.progressSendToHis,
  ]);

  return { handleOpenPdf, handleSendToHis, progressOpenPdf, progressSendToHis };
}

export type SourceSelected<T extends DocsFormName> = {
  contradictions?: Contradictions;
  fieldName: keyof DocsFormDataMapped<T>;
  sourceFields: SourceFields;
};

const useSelectedSource = <T extends DocsFormName>({
  docsFillFormResult,
  headerBannerAction,
  onSourceSelected,
  savedMetadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  savedMetadata: FormMetadata<T> | undefined;
  headerBannerAction: HeaderBannerAction;
  onSourceSelected?: () => void;
}) => {
  const [selectedSource, setSelectedSource] = useState<
    SourceSelected<T> | undefined
  >(undefined);

  const [feedbackOverrides, setFeedbackOverrides] = useState<
    Record<string, Pick<SourceFields, "rating" | "rating_description">>
  >({});

  const contradictions = useMemo(() => {
    const savedParsed =
      typeof savedMetadata?.contradictions?.decrypted === "string"
        ? (() => {
            try {
              return JSON.parse(
                savedMetadata.contradictions.decrypted,
              ) as Record<string, Contradictions>;
            } catch {
              return {};
            }
          })()
        : {};

    return Object.entries(docsFillFormResult?.form ?? {}).reduce<
      Record<string, Contradictions | undefined>
    >(
      (acc, [key, { contradictions: rawContradictions, source_documents }]) => {
        const enriched = rawContradictions && {
          ...rawContradictions,
          candidates: rawContradictions.candidates?.map((candidate) => ({
            ...candidate,
            source_documents: candidate.source_documents?.map(
              (doc) => source_documents?.find((d) => d.id === doc.id) ?? doc,
            ),
          })),
        };
        return { ...acc, [key]: enriched ?? savedParsed[key] };
      },
      { ...savedParsed },
    );
  }, [docsFillFormResult?.form, savedMetadata]);

  const sourceFields = useMemo(() => {
    const isInOverwriteWarning =
      headerBannerAction.banner === HEADER_BANNERS.IMPORT_WARNING;

    const base = Object.entries(docsFillFormResult?.form ?? {}).reduce(
      (acc, [key, inferredValue]) => ({
        ...acc,
        [key]: mergeSourceFieldMetadata({
          inferredValue,
          isInOverwriteWarning,
          savedFieldMetadata:
            savedMetadata?.source_fields?.[key as keyof DocsFormDataMapped<T>],
        }),
      }),
      (savedMetadata?.source_fields ?? {}) as FormMetadata<T>["source_fields"],
    );

    if (Object.keys(feedbackOverrides).length === 0) return base;

    return applyFeedbackOverridesToSourceFields<T>({
      base,
      feedbackOverrides,
    });
  }, [
    docsFillFormResult?.form,
    feedbackOverrides,
    headerBannerAction,
    savedMetadata,
  ]);

  const setFieldRating = useCallback(
    (fieldName: string, rating: DocsFeedbackRatingTypes | null) => {
      setFeedbackOverrides((prev) => ({
        ...prev,
        [fieldName]: { ...prev[fieldName], rating: rating ?? undefined },
      }));
    },
    [],
  );

  const setFieldRatingDescription = useCallback(
    (fieldName: string, description: string) => {
      setFeedbackOverrides((prev) => ({
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          rating_description: description,
        },
      }));
    },
    [],
  );

  const handleSetSelectedSource = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      setSelectedSource({
        contradictions: contradictions[fieldName],
        fieldName,
        sourceFields: sourceFields[fieldName] ?? {},
      });
      onSourceSelected?.();
    },
    [contradictions, onSourceSelected, sourceFields],
  );

  const isSourceSelected = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return selectedSource?.fieldName === fieldName;
    },
    [selectedSource],
  );

  const getSourceFields = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>): SourceFields | undefined => {
      return sourceFields[fieldName];
    },
    [sourceFields],
  );

  const getContradictionFields = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return contradictions[fieldName as string];
    },
    [contradictions],
  );

  const resetSelectedSource = useCallback(() => {
    setSelectedSource(undefined);
  }, []);

  const resetFeedback = useCallback(() => {
    setFeedbackOverrides({});
  }, []);

  return {
    contradictions,
    getContradictionFields,
    getSourceFields,
    handleSetSelectedSource,
    hasFeedbackChanges: Object.keys(feedbackOverrides).length > 0,
    isSourceSelected,
    resetFeedback,
    resetSelectedSource,
    selectedSource,
    setFieldRating,
    setFieldRatingDescription,
    sourceFields,
  } as const;
};

export type SourceInformation<T extends DocsFormName> = ReturnType<
  typeof useSelectedSource<T>
>;

const useFooterBanner = () => {
  const [footerBanner, setFooterBanner] = useState<FooterBannerType | null>(
    null,
  );

  const footerBannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSetFooterBanner = useCallback(
    (banner: FooterBannerType | null) => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }

      setFooterBanner(banner);

      if (banner) {
        footerBannerTimeoutRef.current = setTimeout(() => {
          setFooterBanner(null);
        }, 5000);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }
    };
  }, []);

  return { footerBanner, setFooterBanner: handleSetFooterBanner };
};

export function useDocsForm<T extends DocsFormName>({
  auction,
  auctionForms,
  consultation,
  files,
  formName,
  onFormSaved,
  onSourceSelected,
  patient,
}: {
  auction: Auction | undefined;
  auctionForms: AuctionForm[];
  consultation: ClinicalConsultation | undefined;
  files: { name: string; file: FileState }[];
  formName: T;
  onFormSaved?: () => void;
  onSourceSelected?: () => void;
  patient: Patient;
}) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const [headerBannerAction, setHeaderBannerAction] =
    useState<HeaderBannerAction>({
      banner: null,
    });

  const [footerScreen, setFooterScreen] = useState<DocsFormFooterScreen>(
    DOCS_FORM_FOOTER_SCREENS.DEFAULT,
  );
  const { footerBanner, setFooterBanner } = useFooterBanner();

  const [importFormProgress, setImportFormProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const importModeRef = useRef<{
    editedFieldKeys: Set<string>;
    mode: RefreshMode | undefined;
  }>({ editedFieldKeys: new Set(), mode: undefined });

  // Fields whose previous metadata must be preserved across fills. Populated
  // when the user picks "refresh unedited fields" so the new AI inference for
  // those fields is ignored downstream (source button, save metadata, etc.).
  const [preservedFieldKeys, setPreservedFieldKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const { extractOcrStatus } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const noDocumentsExtracted = !extractOcrStatus.result?.documents?.some(
    (doc) => doc.status === EXTRACT_OCR_STATUS.EXTRACTED,
  );

  const {
    form,
    formData,
    metadata: savedMetadata,
  } = useGetFormData({
    auctionForms,
    consultationForms: consultation?.forms ?? [],
    formName,
  });

  const hasPreviousFormValue = !!formData;

  const formFileType = FORM_NAME_TO_FILE_TYPE[formName];
  const hasExistingFormPdf = files.some(
    ({ file }) =>
      !isFileHISStateful(file) && file.recare_file_type === formFileType,
  );

  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const { config, skipRequiredValidation } = useGetFormFieldConfig(
    formName,
    translations,
  );

  const initialValues = config.getInitialValue({
    auctionForms,
    patient,
    value: formData,
  });

  const methods = useForm<DocsFormDataMapped<T>>({
    defaultValues: initialValues,
    disabled:
      (footerScreen !== DOCS_FORM_FOOTER_SCREENS.DEFAULT &&
        footerScreen !== DOCS_FORM_FOOTER_SCREENS.SAVE_FORM_CONFIRMATION) ||
      isLoading(importFormProgress),
    resolver: zodResolver(
      config.schema as z.ZodType<DocsFormDataMapped<T>, DocsFormDataMapped<T>>,
    ),
  });

  const onError = useCallback(() => {
    setImportFormProgress(QUERY_PROGRESS_FAILED);
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACT_FAILED });
    trackEvent({
      form_name: formName,
      name: TRACK_EVENTS.DOCS_FILL_FORM_RESULT,
      outcome: DOCS_FILL_FORM_OUTCOME.FAILED,
    });
  }, [trackEvent, formName]);

  const onPending = useCallback(() => {
    setImportFormProgress(QUERY_PROGRESS_PENDING);
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACTING });
  }, []);

  const onSuccess = useCallback(
    (result: DocsFormStatusResponse) => {
      if (!result.form_stats?.filled_fields) {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        setHeaderBannerAction({
          banner: HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED,
        });
        trackEvent({
          filled_fields: 0,
          form_name: formName,
          name: TRACK_EVENTS.DOCS_FILL_FORM_RESULT,
          outcome: DOCS_FILL_FORM_OUTCOME.NO_MEANINGFUL_INFORMATION,
        });
        return;
      }

      const { editedFieldKeys, mode } = importModeRef.current;
      const isUneditedOnly = mode === REFRESH_MODE.UNEDITED_FIELDS;
      const isExplicitDropdownChoice = mode !== undefined;

      if (
        !isExplicitDropdownChoice &&
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING
      ) {
        const overwriteFields = Object.entries(result.form).filter(
          ([field, { value }]) => {
            if (value == null || value === "") return false;
            const currentValue = methods.getValues(
              field as Path<DocsFormDataMapped<T>>,
            );
            return currentValue && currentValue !== value;
          },
        );

        if (overwriteFields.length > 0) {
          setImportFormProgress(QUERY_PROGRESS_SUCCEED);
          setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
          return;
        }
      }

      const entriesToApply = Object.entries(result.form).filter(
        ([field, { value }]) => {
          if (value == null || value === "") return false;
          if (isUneditedOnly && editedFieldKeys.has(field)) return false;
          return true;
        },
      );

      if (entriesToApply.length === 0) {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        setHeaderBannerAction({
          banner: HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED,
        });
        return;
      }

      setHeaderBannerAction({
        banner: HEADER_BANNERS.EXTRACT_COMPLETED,
        context: {
          numFilledFields: entriesToApply.length,
        },
      });
      flushSync(() => {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
      });
      entriesToApply.forEach(([field, { value }]) => {
        methods.setValue(
          field as Path<DocsFormDataMapped<T>>,
          value as PathValue<
            DocsFormDataMapped<T>,
            Path<DocsFormDataMapped<T>>
          >,
          {
            shouldDirty: true,
          },
        );
      });
      trackEvent({
        filled_fields: result.form_stats.filled_fields,
        form_name: formName,
        name: TRACK_EVENTS.DOCS_FILL_FORM_RESULT,
        outcome: DOCS_FILL_FORM_OUTCOME.SUCCEEDED,
      });
    },
    [hasPreviousFormValue, headerBannerAction, methods, trackEvent, formName],
  );

  const {
    fillForm: docsFillForm,
    resetResult: resetDocsFillFormResultRaw,
    result: docsFillFormResultRaw,
  } = useDocsFillForm({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    onError,
    onPending,
    onSuccess,
    patientId: patient.id,
    skip: noDocumentsExtracted,
  });

  const docsFillFormResult = useMemo<DocsFormStatusResponse | null>(() => {
    if (!docsFillFormResultRaw?.form || preservedFieldKeys.size === 0) {
      return docsFillFormResultRaw;
    }
    const filteredForm: DocsFormStatusResponse["form"] = {};
    for (const [key, entry] of Object.entries(docsFillFormResultRaw.form)) {
      if (!preservedFieldKeys.has(key)) {
        filteredForm[key] = entry;
      }
    }
    return { ...docsFillFormResultRaw, form: filteredForm };
  }, [docsFillFormResultRaw, preservedFieldKeys]);

  const resetDocsFillFormResult = useCallback(() => {
    resetDocsFillFormResultRaw();
    setPreservedFieldKeys(new Set());
  }, [resetDocsFillFormResultRaw]);

  const sourceInformation = useSelectedSource<T>({
    docsFillFormResult,
    headerBannerAction,
    onSourceSelected,
    savedMetadata,
  });

  const hasExtractedDocumentsAfterLastFormSave = useMemo(() => {
    const formUpdatedAt = form?.updated_at;
    if (!formUpdatedAt) {
      return false;
    }

    return !!extractOcrStatus.result?.documents?.some(
      (doc) =>
        doc.status === EXTRACT_OCR_STATUS.EXTRACTED &&
        doc.status_updated_at > formUpdatedAt,
    );
  }, [extractOcrStatus.result?.documents, form?.updated_at]);

  const [
    isNewExtractedDocumentsBannerDismissed,
    setIsNewExtractedDocumentsBannerDismissed,
  ] = useState(false);

  const hasNewExtractedDocuments =
    hasExtractedDocumentsAfterLastFormSave &&
    !isNewExtractedDocumentsBannerDismissed;

  const dismissNewExtractedDocumentsBanner = useCallback(() => {
    setIsNewExtractedDocumentsBannerDismissed(true);
  }, []);

  const { handleAuctionToFormData, importFormData } = useImportFormData({
    auction,
    formGetters: config.auctionToForm?.(getOntology, translations),
    formOnChange: (value, field) => {
      if (value == null || value === "") return;
      methods.setValue(field as Path<DocsFormDataMapped<T>>, value, {
        shouldDirty: true,
      });
    },
    formValueGetter: (field) => {
      return methods.getValues(field as Path<DocsFormDataMapped<T>>);
    },
  });

  const handleImportData = async (mode?: RefreshMode) => {
    try {
      if (hasNewExtractedDocuments) {
        dismissNewExtractedDocumentsBanner();
      }
      setImportFormProgress(QUERY_PROGRESS_PENDING);

      // Snapshot which fields the user has manually edited BEFORE we start
      // the new fill, so onSuccess can skip them when the user chose
      // "refresh unedited fields".
      const editedFieldKeys =
        mode === REFRESH_MODE.UNEDITED_FIELDS
          ? getManuallyEditedFieldKeys<T>({
              currentValues: methods.getValues(),
              previousFillResult: docsFillFormResult,
              previousFormData: formData,
              savedMetadata,
            })
          : new Set<string>();
      importModeRef.current = { editedFieldKeys, mode };

      // Lock in (or clear) preserved fields *before* the new fill arrives
      setPreservedFieldKeys(
        mode === REFRESH_MODE.UNEDITED_FIELDS ? editedFieldKeys : new Set(),
      );

      const { formData: importedFormData, isOverwriting } =
        handleAuctionToFormData();

      const shouldShowOverwriteWarning =
        mode === undefined &&
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING &&
        isOverwriting;

      if (shouldShowOverwriteWarning) {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
        return;
      }

      if (isDocsAndExtractActive) {
        if (noDocumentsExtracted) {
          importFormData();
          setHeaderBannerAction({ banner: null });
          setImportFormProgress(QUERY_PROGRESS_SUCCEED);
          return;
        }
        await docsFillForm({
          formData: importedFormData,
          formName,
        });
        return;
      }

      if (isDischargeLetterFormName(formName)) {
        await docsFillForm({
          formData: importedFormData,
          formName,
        });
        return;
      }

      importFormData();
      setHeaderBannerAction({ banner: null });
      setImportFormProgress(QUERY_PROGRESS_SUCCEED);
    } catch (error) {
      setImportFormProgress(QUERY_PROGRESS_FAILED);
      console.error("error with import form data", error);
    }
  };

  const { handleOpenPdf, handleSendToHis, progressOpenPdf, progressSendToHis } =
    usePdfHandlers({
      auctionForms,
      config,
      files,
      footerScreen,
      formData: formData as DocsFormDataMapped<T>,
      formId: form?.id,
      formName,
      formType: form?.form_type,
      isDirty: methods.formState.isDirty,
      patient,
      setFooterBanner,
      setFooterScreen,
    });

  const handleBack = () => {
    setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
  };

  let docsFileParams: FileParams;
  if (auction)
    docsFileParams = `auction_id=${auction.id}&scope=${FILE_SCOPE.PATIENT_FILES}`;
  else if (consultation)
    docsFileParams = `clinical_consultation_id=${consultation.id}&scope=${FILE_SCOPE.PATIENT_FILES}`;
  else
    docsFileParams = `patient_id=${patient.id}&scope=${FILE_SCOPE.PATIENT_FILES}`;

  const { progress: progressUploadFormPdf, uploadFormPdf } = useFormPdfUpload({
    auction,
    auctionForms,
    fileParams: docsFileParams,
    formName,
    patient,
  });

  const { saveForm, saveFormProgress } = useFormSave({
    auction,
    auctionForms,
    consultation,
    docsFillFormResult,
    formName,
    patient,
    previousFormData: formData,
    sourceInformation,
  });

  const handleSubmit = methods.handleSubmit(
    async (data: DocsFormDataMapped<T>) => {
      switch (footerScreen) {
        case DOCS_FORM_FOOTER_SCREENS.SAVE_FORM_CONFIRMATION: {
          const saved = await saveForm(
            data,
            methods.formState.isDirty || sourceInformation.hasFeedbackChanges,
          );
          if (!saved) {
            setFooterBanner(FOOTER_BANNERS.SAVE_ERROR);
            handleBack();
            break;
          }
          methods.reset(
            config.getInitialValue({
              auctionForms,
              patient,
              value: data,
            }),
          );
          // Drop the cached fill result so source-info derives purely from the
          // freshly-saved metadata
          resetDocsFillFormResult();
          sourceInformation.resetFeedback();
          onFormSaved?.();
          setFooterBanner(FOOTER_BANNERS.SAVE_FORM_SUCCESS);
          handleBack();
          break;
        }
        case DOCS_FORM_FOOTER_SCREENS.UPLOAD_PDF_CONFIRMATION: {
          if (!confirmationChecked || !formData) return;
          const uploadResult = await uploadFormPdf(formData, config.pdfConfig);
          if (!uploadResult) {
            setFooterBanner(FOOTER_BANNERS.SAVE_ERROR);
            handleBack();
            return;
          }
          handleBack();
          setFooterBanner(
            hasExistingFormPdf
              ? FOOTER_BANNERS.OVERWRITE_PDF_SUCCESS
              : FOOTER_BANNERS.CREATE_PDF_SUCCESS,
          );
          resetDocsFillFormResult();
          break;
        }
      }
    },
  );

  const { deleteFormProgress, onDelete } = useFormDelete({
    auction,
    consultation,
    form,
    formName,
    patient,
  });

  const handleDeleteForm = async () => {
    if (hasPreviousFormValue) {
      try {
        const deleted = await onDelete();
        if (!deleted) {
          setFooterBanner(FOOTER_BANNERS.DELETE_ERROR);
          handleBack();
        }
      } catch (error) {
        console.error(error);
      }
    }
    methods.reset(config.getInitialValue({ auctionForms, patient }));
    setImportFormProgress(QUERY_PROGRESS_NOT_STARTED);
    setFooterBanner(FOOTER_BANNERS.DELETE_SUCCESS);
    setHeaderBannerAction({ banner: null });
    resetDocsFillFormResult();
    handleBack();
  };

  const onCloseHeaderBanner = () => {
    setHeaderBannerAction({ banner: null });
  };

  useEffect(() => {
    setConfirmationChecked(false);
  }, [footerScreen]);

  useEffect(() => {
    if (!isDocsAndExtractActive && !isDischargeLetterActive) return;

    if (noDocumentsExtracted) {
      setHeaderBannerAction({ banner: HEADER_BANNERS.NO_FILES_WARNING });
      return;
    }

    setHeaderBannerAction({ banner: null });
  }, [isDischargeLetterActive, isDocsAndExtractActive, noDocumentsExtracted]);

  const canImportData = useMemo(() => {
    if (isLoading(importFormProgress)) return false;
    if (isDischargeLetterFormName(formName)) {
      if (noDocumentsExtracted) return false;
    }
    return true;
  }, [formName, importFormProgress, noDocumentsExtracted]);

  return {
    canImportData,
    config,
    confirmationChecked,
    docsFillFormResult,
    footerBanner,
    footerScreen,
    form,
    formData,
    handleBack,
    handleDeleteForm,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasExistingFormPdf,
    hasNewExtractedDocuments,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData: handleImportData,
    importFormProgress,
    methods,
    noDocumentsExtracted,
    onCloseHeaderBanner,
    onCloseNewExtractedDocumentsBanner: dismissNewExtractedDocumentsBanner,
    onSubmitProgress: composeProgress([
      saveFormProgress,
      progressUploadFormPdf,
    ]),
    progressDeleteForm: deleteFormProgress,
    progressOpenPdf,
    progressSaveForm: saveFormProgress,
    progressSendToHis,
    progressUploadFormPdf,
    savedMetadata,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  };
}
